'use client';

import React, { useState, useEffect, useCallback } from 'react';


// ─── TYPES ────────────────────────────────────────────────────────────────────

export interface NiyyahRecord {
  actionType: 'plant_module' | 'begin_quest' | 'submit_observation' | 'mrg_bridge' | 'community_share';
  actionId: string;
  actionLabel: string;
  niyyahText: string;
  orientation?: 'understand' | 'serve' | 'remember' | null;
  timestamp: string;
  completed?: boolean;
  reflection?: string;
}

export type NiyyahOrientation = 'understand' | 'serve' | 'remember' | null;

// ─── ORIENTATION DETECTION ────────────────────────────────────────────────────

function detectOrientation(text: string): NiyyahOrientation {
  const lower = text.toLowerCase();
  if (/understand|learn|know|study|comprehend|why|how|meaning|etymology|history|context/.test(lower)) return 'understand';
  if (/serve|help|action|do|work|assist|support|community|resource|bridge|connect/.test(lower)) return 'serve';
  if (/remember|dhikr|allah|remind|reflect|contemplate|tafakkur|presence|conscious|aware/.test(lower)) return 'remember';
  return null;
}

// ─── STORAGE HELPERS ──────────────────────────────────────────────────────────

function saveNiyyah(record: NiyyahRecord): void {
  if (typeof window === 'undefined') return;
  const existing = JSON.parse(localStorage.getItem('haal_niyyah_records') || '[]') as NiyyahRecord[];
  const idx = existing.findIndex(r => r.actionId === record.actionId);
  if (idx >= 0) existing[idx] = record;
  else existing.push(record);
  localStorage.setItem('haal_niyyah_records', JSON.stringify(existing));
}

export function getNiyyahForAction(actionId: string): NiyyahRecord | null {
  if (typeof window === 'undefined') return null;
  const records = JSON.parse(localStorage.getItem('haal_niyyah_records') || '[]') as NiyyahRecord[];
  return records.find(r => r.actionId === actionId) || null;
}

export function hasNiyyahForAction(actionId: string): boolean {
  return !!getNiyyahForAction(actionId);
}

// ─── ACTION TYPE LABELS ───────────────────────────────────────────────────────

const ACTION_LABELS: Record<NiyyahRecord['actionType'], string> = {
  plant_module: 'planting a module',
  begin_quest: 'beginning a quest',
  submit_observation: 'submitting an observation',
  mrg_bridge: 'bridging to a resource',
  community_share: 'sharing to the Feed',
};

const ORIENTATION_SUGGESTIONS = [
  'I am planting this to understand',
  'I am planting this to serve',
  'I am planting this to remember',
];

// ─── NIYYAH FIELD MODAL ───────────────────────────────────────────────────────

interface NiyyahFieldProps {
  open: boolean;
  actionType: NiyyahRecord['actionType'];
  actionId: string;
  actionLabel: string;
  onConfirm: (niyyah: NiyyahRecord) => void;
  onSkip: () => void;
}

export function NiyyahField({ open, actionType, actionId, actionLabel, onConfirm, onSkip }: NiyyahFieldProps) {
  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (open) setText('');
  }, [open]);

  const handleSubmit = useCallback(() => {
    if (text.trim().length < 10) return;
    const orientation = actionType === 'plant_module' ? detectOrientation(text) : null;
    const record: NiyyahRecord = {
      actionType,
      actionId,
      actionLabel,
      niyyahText: text.trim(),
      orientation,
      timestamp: new Date().toISOString(),
      completed: false,
    };
    saveNiyyah(record);
    onConfirm(record);
  }, [text, actionType, actionId, actionLabel, onConfirm]);

  if (!open) return null;

  const isPlanting = actionType === 'plant_module';
  const canSubmit = text.trim().length >= 10;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9500,
        background: 'rgba(0,0,0,0.92)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        animation: 'niyyahIn 0.3s ease',
      }}
    >
      <div style={{ maxWidth: 520, width: '100%' }}>
        {/* Bismillah */}
        <p
          style={{
            fontFamily: "'Amiri', serif",
            fontSize: 28,
            color: '#c9a84c',
            textAlign: 'center',
            direction: 'rtl',
            marginBottom: 8,
          }}
        >
          بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ
        </p>

        {/* Action context */}
        <p
          style={{
            fontFamily: 'monospace',
            fontSize: 10,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: 'rgba(201,168,76,0.4)',
            textAlign: 'center',
            marginBottom: 6,
          }}
        >
          You are about to {ACTION_LABELS[actionType]}
        </p>
        {actionLabel && (
          <p
            style={{
              fontFamily: "'Amiri', serif",
              fontSize: 15,
              color: 'rgba(212,197,169,0.7)',
              textAlign: 'center',
              marginBottom: 28,
              fontStyle: 'italic',
            }}
          >
            {actionLabel}
          </p>
        )}

        {/* Niyyah prompt */}
        <p
          style={{
            fontFamily: "'Amiri', 'Georgia', serif",
            fontSize: 20,
            color: '#c9a84c',
            textAlign: 'center',
            marginBottom: 16,
          }}
        >
          What is your niyyah?
        </p>

        {/* Orientation suggestions (planting only) */}
        {isPlanting && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginBottom: 16 }}>
            {ORIENTATION_SUGGESTIONS.map(s => (
              <button
                key={s}
                onClick={() => setText(s)}
                style={{
                  fontFamily: 'monospace',
                  fontSize: 9,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  padding: '6px 12px',
                  borderRadius: 20,
                  background: text === s ? 'rgba(201,168,76,0.15)' : 'rgba(201,168,76,0.04)',
                  border: `1px solid ${text === s ? 'rgba(201,168,76,0.5)' : 'rgba(201,168,76,0.15)'}`,
                  color: text === s ? '#c9a84c' : 'rgba(201,168,76,0.4)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {/* Text area */}
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          placeholder="In your own words — why are you doing this?"
          rows={4}
          style={{
            width: '100%',
            background: 'rgba(255,255,255,0.03)',
            border: `1px solid ${focused ? 'rgba(201,168,76,0.4)' : 'rgba(201,168,76,0.15)'}`,
            borderRadius: 8,
            padding: '14px 16px',
            color: '#d4c5a9',
            fontSize: 14,
            lineHeight: 1.7,
            fontFamily: "'Georgia', serif",
            resize: 'vertical',
            outline: 'none',
            transition: 'border-color 0.2s',
            marginBottom: 8,
            boxSizing: 'border-box',
          }}
        />
        <p
          style={{
            fontFamily: 'monospace',
            fontSize: 9,
            color: text.trim().length < 10 ? 'rgba(212,197,169,0.3)' : 'rgba(45,122,71,0.6)',
            marginBottom: 20,
            textAlign: 'right',
          }}
        >
          {text.trim().length < 10 ? `${10 - text.trim().length} more characters` : '✓ Ready'}
        </p>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              fontFamily: 'monospace',
              fontSize: 11,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              padding: '14px 32px',
              borderRadius: 8,
              background: canSubmit ? 'rgba(201,168,76,0.12)' : 'rgba(201,168,76,0.04)',
              border: `1px solid ${canSubmit ? 'rgba(201,168,76,0.5)' : 'rgba(201,168,76,0.15)'}`,
              color: canSubmit ? '#c9a84c' : 'rgba(201,168,76,0.3)',
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s',
            }}
          >
            Bismillah →
          </button>
          <button
            onClick={onSkip}
            style={{
              fontFamily: 'monospace',
              fontSize: 10,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              padding: '14px 20px',
              borderRadius: 8,
              background: 'transparent',
              border: '1px solid rgba(212,197,169,0.1)',
              color: 'rgba(212,197,169,0.3)',
              cursor: 'pointer',
            }}
          >
            Skip
          </button>
        </div>

        {/* Hadith note */}
        <p
          style={{
            fontFamily: 'monospace',
            fontSize: 9,
            letterSpacing: '0.08em',
            color: 'rgba(212,197,169,0.2)',
            textAlign: 'center',
            marginTop: 20,
            lineHeight: 1.6,
          }}
        >
          &ldquo;Actions are only by intentions.&rdquo; — Sahih Bukhari, Hadith 1
        </p>
      </div>
      <style>{`
        @keyframes niyyahIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

// ─── MUHASABA MOMENT ──────────────────────────────────────────────────────────

interface MuhasabaMomentProps {
  open: boolean;
  niyyahRecord: NiyyahRecord | null;
  onFulfilled: () => void;
  onRevealed: (reflection: string) => void;
}

export function MuhasabaMoment({ open, niyyahRecord, onFulfilled, onRevealed }: MuhasabaMomentProps) {
  const [showReflection, setShowReflection] = useState(false);
  const [reflection, setReflection] = useState('');

  useEffect(() => {
    if (open) { setShowReflection(false); setReflection(''); }
  }, [open]);

  if (!open || !niyyahRecord) return null;

  const handleRevealed = () => {
    if (reflection.trim().length > 0) {
      const updated = { ...niyyahRecord, reflection: reflection.trim(), completed: true };
      saveNiyyah(updated);
      onRevealed(reflection.trim());
    } else {
      onRevealed('');
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9400,
        background: 'rgba(0,0,0,0.88)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        animation: 'niyyahIn 0.4s ease',
      }}
    >
      <div style={{ maxWidth: 500, width: '100%', textAlign: 'center' }}>
        <p
          style={{
            fontFamily: 'monospace',
            fontSize: 9,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: 'rgba(201,168,76,0.4)',
            marginBottom: 16,
          }}
        >
          مُحَاسَبَة — Self-Accounting
        </p>
        <p
          style={{
            fontFamily: "'Amiri', 'Georgia', serif",
            fontSize: 18,
            color: 'rgba(212,197,169,0.7)',
            marginBottom: 12,
          }}
        >
          You began this with the intention:
        </p>
        <div
          style={{
            background: 'rgba(201,168,76,0.04)',
            border: '1px solid rgba(201,168,76,0.2)',
            borderRadius: 8,
            padding: '16px 20px',
            marginBottom: 24,
          }}
        >
          <p
            style={{
              fontFamily: "'Georgia', serif",
              fontSize: 15,
              color: '#c9a84c',
              fontStyle: 'italic',
              lineHeight: 1.7,
            }}
          >
            &ldquo;{niyyahRecord.niyyahText}&rdquo;
          </p>
        </div>

        <p
          style={{
            fontFamily: "'Amiri', 'Georgia', serif",
            fontSize: 16,
            color: 'rgba(212,197,169,0.65)',
            lineHeight: 1.7,
            marginBottom: 28,
          }}
        >
          Has the journey fulfilled the intention, or revealed something the intention didn&apos;t anticipate?
        </p>

        {!showReflection ? (
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={onFulfilled}
              style={{
                fontFamily: "'Amiri', serif",
                fontSize: 16,
                padding: '14px 28px',
                borderRadius: 8,
                background: 'rgba(45,122,71,0.1)',
                border: '1px solid rgba(45,122,71,0.4)',
                color: '#2d7a47',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              الحمد لله — It did
            </button>
            <button
              onClick={() => setShowReflection(true)}
              style={{
                fontFamily: 'monospace',
                fontSize: 10,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                padding: '14px 24px',
                borderRadius: 8,
                background: 'rgba(201,168,76,0.06)',
                border: '1px solid rgba(201,168,76,0.25)',
                color: '#c9a84c',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
            >
              It revealed something more →
            </button>
          </div>
        ) : (
          <div>
            <textarea
              value={reflection}
              onChange={e => setReflection(e.target.value)}
              placeholder="What did it reveal?"
              rows={4}
              style={{
                width: '100%',
                background: 'rgba(255,255,255,0.03)',
                border: '1px solid rgba(201,168,76,0.2)',
                borderRadius: 8,
                padding: '14px 16px',
                color: '#d4c5a9',
                fontSize: 14,
                lineHeight: 1.7,
                fontFamily: "'Georgia', serif",
                resize: 'vertical',
                outline: 'none',
                marginBottom: 16,
                boxSizing: 'border-box',
              }}
            />
            <button
              onClick={handleRevealed}
              style={{
                fontFamily: 'monospace',
                fontSize: 10,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                padding: '12px 28px',
                borderRadius: 8,
                background: 'rgba(201,168,76,0.1)',
                border: '1px solid rgba(201,168,76,0.4)',
                color: '#c9a84c',
                cursor: 'pointer',
              }}
            >
              Record →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── NIYYAH GATE INLINE NOTE ──────────────────────────────────────────────────

interface NiyyahGateNoteProps {
  actionId: string;
  onAddNiyyah: () => void;
}

export function NiyyahGateNote({ actionId, onAddNiyyah }: NiyyahGateNoteProps) {
  const hasNiyyah = hasNiyyahForAction(actionId);
  if (hasNiyyah) return null;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '10px 14px',
        borderRadius: 8,
        background: 'rgba(201,168,76,0.04)',
        border: '1px solid rgba(201,168,76,0.15)',
        marginTop: 8,
      }}
    >
      <span style={{ color: 'rgba(201,168,76,0.5)', fontSize: 12, flexShrink: 0, marginTop: 1 }}>◌</span>
      <div>
        <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 11, lineHeight: 1.6, marginBottom: 4 }}>
          Actions are only by intentions. Return to record your niyyah for this action to complete its recognition.
        </p>
        <button
          onClick={onAddNiyyah}
          style={{
            fontFamily: 'monospace',
            fontSize: 9,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            background: 'transparent',
            border: 'none',
            color: 'rgba(201,168,76,0.5)',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          Add niyyah →
        </button>
      </div>
    </div>
  );
}

// ─── NIYYAH DRIFT DETECTOR ────────────────────────────────────────────────────

interface NiyyahDriftPromptProps {
  moduleId: string;
  plantedOrientation: NiyyahOrientation;
  daysSincePlanted: number;
  onDismiss: () => void;
}

export function NiyyahDriftPrompt({ moduleId, plantedOrientation, daysSincePlanted, onDismiss }: NiyyahDriftPromptProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const key = `haal_niyyah_drift_${moduleId}`;
    const lastShown = localStorage.getItem(key);
    const today = new Date().toDateString();
    if (plantedOrientation === 'serve' && daysSincePlanted >= 14 && lastShown !== today) {
      setVisible(true);
      localStorage.setItem(key, today);
    }
  }, [moduleId, plantedOrientation, daysSincePlanted]);

  if (!visible) return null;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '100%',
        left: '50%',
        transform: 'translateX(-50%)',
        marginBottom: 8,
        zIndex: 200,
        maxWidth: 260,
        width: 'max-content',
      }}
    >
      <div
        style={{
          background: '#0a1218',
          border: '1px solid rgba(201,168,76,0.25)',
          borderRadius: 8,
          padding: '12px 14px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
        }}
      >
        <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 11, lineHeight: 1.6, marginBottom: 8 }}>
          Your intention was to serve. What action has this recognition called you to?
        </p>
        <button
          onClick={() => { setVisible(false); onDismiss(); }}
          style={{
            fontFamily: 'monospace',
            fontSize: 8,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            background: 'transparent',
            border: 'none',
            color: 'rgba(201,168,76,0.4)',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          Noted ×
        </button>
      </div>
      {/* Arrow */}
      <div style={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '6px solid rgba(201,168,76,0.25)', margin: '0 auto' }} />
    </div>
  );
}

// ─── NIYYAH ORIENTATION BADGE ─────────────────────────────────────────────────

interface NiyyahOrientationBadgeProps {
  orientation: NiyyahOrientation;
}

export function NiyyahOrientationBadge({ orientation }: NiyyahOrientationBadgeProps) {
  if (!orientation) return null;

  const config: Record<NonNullable<NiyyahOrientation>, { label: string; color: string; arabic: string }> = {
    understand: { label: 'To Understand', color: '#2d8a87', arabic: 'لِلْفَهْم' },
    serve: { label: 'To Serve', color: '#2d7a47', arabic: 'لِلْخِدْمَة' },
    remember: { label: 'To Remember', color: '#c9a84c', arabic: 'لِلذِّكْر' },
  };

  const c = config[orientation];

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        fontFamily: 'monospace',
        fontSize: 8,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        padding: '3px 8px',
        borderRadius: 10,
        background: `${c.color}10`,
        border: `1px solid ${c.color}30`,
        color: c.color,
      }}
    >
      <span style={{ fontFamily: "'Amiri', serif", fontSize: 10 }}>{c.arabic}</span>
      {c.label}
    </span>
  );
}
